import { getApiUrl } from "src/environment";

const PING_TIMEOUT_MS = 5000;
const PING_INTERVAL_MS = 15000;

let apiReachable = false;
let webMonitorStarted = false;
let pingInFlight: Promise<boolean> | null = null;
const listeners = new Set<(online: boolean) => void>();

const isNetworkError = (error: unknown) => {
  const message = String((error as Error)?.message ?? error).toLowerCase();
  return ["network error", "failed to fetch", "net::"].some(netErr =>
    message.includes(netErr),
  );
};

const setApiReachable = (reachable: boolean) => {
  if (apiReachable === reachable) {
    return;
  }
  apiReachable = reachable;
  listeners.forEach(cb => cb(reachable));
};

export const pingApi = async (): Promise<boolean> => {
  if (window.api) {
    return false;
  }

  if (pingInFlight) {
    return pingInFlight;
  }

  pingInFlight = (async () => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), PING_TIMEOUT_MS);

    try {
      const response = await fetch(getApiUrl(), {
        method: "POST",
        mode: "cors",
        body: JSON.stringify({ request: "ping" }),
        signal: controller.signal,
      });
      await response.json();
      setApiReachable(true);
      return true;
    } catch (error) {
      if (
        isNetworkError(error) ||
        (error as Error)?.name === "AbortError" ||
        error instanceof SyntaxError
      ) {
        setApiReachable(false);
        return false;
      }
      setApiReachable(false);
      return false;
    } finally {
      clearTimeout(timeoutId);
      pingInFlight = null;
    }
  })();

  return pingInFlight;
};

/**
 * API reachability check for reducers, thunks, and utilities.
 * Uses cached ping result from the desktop bridge or web monitor.
 */
export const isOnline = () => {
  try {
    if (window.api?.isOnline) {
      return window.api.isOnline();
    }
  } catch {
    // Fall back to web ping cache when the desktop bridge is unavailable.
  }

  return apiReachable;
};

/**
 * OS-level link status. Use only for non-API flows (e.g. auto-update).
 */
export const isLinkOnline = () => {
  try {
    if (window.api?.isLinkOnline) {
      return window.api.isLinkOnline();
    }
  } catch {
    // Fall back to navigator when the desktop bridge is unavailable.
  }

  return typeof navigator !== "undefined" ? navigator.onLine : true;
};

export const onApiReachabilityChange = (cb: (online: boolean) => void) => {
  if (window.api?.onApiReachabilityChange) {
    window.api.onApiReachabilityChange(cb);
    return () => {};
  }

  listeners.add(cb);
  return () => listeners.delete(cb);
};

export const startWebApiReachabilityMonitor = () => {
  if (webMonitorStarted || window.api) {
    return;
  }
  webMonitorStarted = true;

  pingApi();

  setInterval(() => {
    pingApi();
  }, PING_INTERVAL_MS);

  window.addEventListener("online", () => {
    pingApi();
  });
};
